import { MoreOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Col, Dropdown, Image, Menu, Progress, Row, Space, Upload, Alert, Tag, Modal, Table, Spin } from 'antd';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { predictValue, previewModel, clearPredictResult, clearPredictConfig } from 'reduxStore/actions/modelAction';
import styled from 'styled-components';
import AudioUpload from 'assets/images/audio@2x.png';
import CSVUpload from 'assets/images/csv@2x.png';
import CSVIcon from 'assets/images/csvicon@2x.png';
import ImageUpload from 'assets/images/img@2x.png';
import AudioPlayer from 'components/etc/AudioPlayer';
import BaseModal from './baseModal';
import Papa from 'papaparse';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toPng } from 'html-to-image';

// Constants
const CHART_HEIGHT = 220;
const TABLE_SCROLL_HEIGHT = 240;
const PREDICTION_RESULTS_MAX_HEIGHT = 250;
const PROGRESS_MAX_VALUE = 150;
const CHART_EXPORT_WIDTH = 800;
const CHART_EXPORT_HEIGHT = 400;
const IMAGE_TYPES = ['JPG', 'JPEG', 'PNG'];
const AUDIO_TYPES = ['WAV', 'MP4', 'M4A', 'MP3'];
const LINE_COLORS = ['#8884d8', '#82ca9d', '#ffc658'];
const PREDICTED_COLOR = '#ffaf02';
const BORDER_COLOR = '#80808080';
const TEXT_COLOR_PRIMARY = '#4d4d4d';
const TEXT_COLOR_SECONDARY = '#808080';

// Utility functions
const downloadFile = (url, filename) => {
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

const createBlobUrl = (blob) => {
	return URL.createObjectURL(blob);
};

const revokeBlobUrl = (url) => {
	URL.revokeObjectURL(url);
};

const parseCSVData = async (url) => {
	try {
		const response = await fetch(url);
		const csvText = await response.text();
		return Papa.parse(csvText, { header: true, skipEmptyLines: true }).data;
	} catch (error) {
		console.error('Error fetching or parsing CSV:', error);
		return [];
	}
};

const createCSVPredictionUpdate = (result, chartData) => ({
	class: 'Predicted',
	predict: 'Completed',
	accuracy: 'CSV',
	predicted: true,
	predictionResult: result?.timeseries_result,
	chartData,
	mae: result?.mae,
	rmse: result?.rmse,
});

const createNonCSVPredictionUpdate = (result, config) => {
	const maxPredict = Math.max(...result?.value);
	const maxIndex = result?.value.indexOf(maxPredict);
	const predictedClass = config?.modelclass[maxIndex];

	return {
		class: predictedClass,
		classIndex: maxIndex,
		predict: maxPredict.toFixed(4),
		f1_score: result.f1_score,
		precision: result.precision,
		recall: result.recall,
		accuracy: result.accuracy,
		confusionMatrix: result.confusion_matrix,
		predicted: true,
		predictionResult: result?.value,
	};
};

const getBase64 = (file, callback) => {
	if (file.type.includes('csv')) {
		const reader = new FileReader();
		reader.onload = (e) => {
			Papa.parse(e.target.result, {
				header: true,
				skipEmptyLines: true,
				complete: (results) => {
					callback(null, results.data);
				},
			});
		};
		reader.readAsText(file);
	} else {
		const reader = new FileReader();
		reader.addEventListener('load', () => callback(reader.result, null));
		reader.readAsDataURL(file);
	}
};

// Helper for table row class names
const getRowClassName = (record, selectedFileKey) => {
	const classNames = [];
	if (record.key === selectedFileKey) {
		classNames.push('selected-row');
	}

	if (record.predicted) {
		classNames.push(record.classIndex === 1 ? 'predicted-class-1' : 'predicted-class-0');
	} else {
		classNames.push('not-predicted');
	}
	return classNames.join(' ');
};

const FileTable = ({ files, columns, setSelectedFileKey, selectedFileKey }) => (
	<StyledTable
		dataSource={files}
		columns={columns}
		pagination={false}
		scroll={{ y: TABLE_SCROLL_HEIGHT }}
		onRow={(record) => ({
			onClick: () => setSelectedFileKey(record.key),
		})}
		rowClassName={(record) => getRowClassName(record, selectedFileKey)}
	/>
);

const LineChartPreview = ({ data, showChart = true }) => {
	if (!data || data.length === 0) {
		return <div>No data to display</div>;
	}

	if (!showChart) {
		return null;
	}

	const keys = Object.keys(data[0]);
	const xKey = keys[0];
	const yKeys = keys.slice(1);
	const lineColors = LINE_COLORS;

	return (
		<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
			<LineChart data={data}>
				<CartesianGrid strokeDasharray="3 3" />
				<XAxis dataKey={xKey} />
				<YAxis />
				<Tooltip />
				<Legend />
				{yKeys.map((key, index) => (
					<Line
						key={key}
						type="monotone"
						dataKey={key}
						stroke={key === 'predicted' ? PREDICTED_COLOR : lineColors[index % lineColors.length]}
					/>
				))}
			</LineChart>
		</ResponsiveContainer>
	);
};

const SingleFilePreview = ({ file, type, onClearFiles }) => {
	switch (type) {
		case 'image':
			return (
				<ImageContainer>
					<Image src={file?.src} style={{ objectFit: 'contain', maxHeight: CHART_HEIGHT }} />
				</ImageContainer>
			);
		case 'audio':
			return <AudioPlayer file={file} onDelete={onClearFiles} />;
		case 'timeseries':
			return <LineChartPreview data={file?.data} />;
		default:
			return (
				<PreviewContainer>
					<Image src={CSVIcon} preview={false} width={100} />
					<div>{file.name}</div>
				</PreviewContainer>
			);
	}
};

const UploadArea = ({ files, uploadProps, getAcceptedType, config, onClearFiles }) => {
	if (files.length === 1) {
		return (
			<Dragger isHaveFile={!!files[0]} {...uploadProps}>
				<SingleFilePreview file={files[0]} type={getAcceptedType.type} onClearFiles={onClearFiles} />
			</Dragger>
		);
	}

	return (
		<Upload.Dragger {...uploadProps}>
			<p className="ant-upload-drag-icon">
				<Image src={getAcceptedType.image} preview={false} width={100} />
			</p>
			<p className="ant-upload-text">Drag and Drop to predict</p>
			<p className="ant-upload-hint">Maximum file size: {config?.maxSize.toUpperCase()}</p>
			<p className="ant-upload-hint">
				or <span style={{ color: PREDICTED_COLOR, fontWeight: 600 }}>Browse file</span>
			</p>
		</Upload.Dragger>
	);
};

const PredictionPanel = ({
	config,
	resultToShow,
	onShowConfusionMatrix,
	selectedFile,
	onDownloadCSV,
	onDownloadChart,
}) => {
	const isCSVFile = selectedFile?.type?.includes('csv');
	const hasChartData = isCSVFile && selectedFile?.chartData;
	const chartRef = useRef(null);

	return (
		<Space direction="vertical" style={{ width: '100%' }}>
			{!isCSVFile && (
				<div>
					<Title>ML Model info</Title>
					<Info>
						Name: <span>{config?.name}</span>
					</Info>
					<Info>
						Description: <span>{config?.description}</span>
					</Info>
				</div>
			)}
			<Title>Prediction results</Title>

			{isCSVFile && hasChartData ? (
				<div ref={chartRef} style={{ marginTop: 20 }}>
					<LineChartPreview data={selectedFile.chartData} showChart={true} />
				</div>
			) : (
				<div style={{ maxHeight: PREDICTION_RESULTS_MAX_HEIGHT, overflowY: 'auto', paddingRight: 5 }}>
					{config?.modelclass?.map((item, index) => (
						<div key={`modelclass-${index}`}>
							<Info>
								<span>
									{item} : {resultToShow ? resultToShow[index] : '-'}
								</span>
							</Info>
							<Progress
								percent={(resultToShow?.[index] / PROGRESS_MAX_VALUE) * 100}
								strokeColor={PREDICTED_COLOR}
								showInfo={false}
							/>
						</div>
					))}
				</div>
			)}
			{resultToShow && (
				<>
					<Space wrap justify="space-between">
						{isCSVFile ? (
							<>
								<ResultTag>MAE: {selectedFile.mae || '-'}</ResultTag>
								<ResultTag>RMSE: {selectedFile.rmse || '-'}</ResultTag>
							</>
						) : (
							<>
								<ResultTag>Accuracy: {selectedFile.accuracy || '-'}</ResultTag>
								<ResultTag>Precision: {selectedFile.precision || '-'}</ResultTag>
								<ResultTag>Recall: {selectedFile.recall || '-'}</ResultTag>
								<ResultTag>F-1 Score: {selectedFile.f1_score || '-'}</ResultTag>
							</>
						)}
					</Space>
					<div
						style={{
							width: '100%',
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 10,
							gap: 10,
						}}
					>
						{isCSVFile ? (
							<>
								<CMButton onClick={onDownloadCSV}>Download CSV</CMButton>
								{hasChartData && (
									<CMButton onClick={() => onDownloadChart(chartRef.current)}>Download PNG</CMButton>
								)}
							</>
						) : (
							<CMButton onClick={onShowConfusionMatrix}>Confusion Matrix</CMButton>
						)}
					</div>
				</>
			)}
		</Space>
	);
};

const ConfusionMatrixModal = ({ visible, onCancel, onDownload, confusionMatrix }) => (
	<Modal centered open={visible} onCancel={onCancel} footer={null}>
		<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
			<Image src={confusionMatrix} preview={false} style={{ borderRadius: 4 }} />

			<Button
				key="download"
				type="primary"
				onClick={onDownload}
				icon={<DownloadOutlined />}
				style={{ marginTop: 20 }}
			>
				Download
			</Button>
		</div>
	</Modal>
);

const PreviewModal = ({
	previewModel,
	clearPredictResult,
	clearPredictConfig,
	visible,
	item,
	config,
	result,
	predicting,
	predictValue,
	...rest
}) => {
	const [files, setFiles] = useState([]);
	const [fileOver, setFileOverLimit] = useState(false);
	const [duplicateFile, setDuplicateFile] = useState(false);
	const [confusionMatrixVisible, setConfusionMatrixVisible] = useState(false);
	const [selectedFileKey, setSelectedFileKey] = useState(null);
	const [predictingFileKey, setPredictingFileKey] = useState(null);
	const [predictionQueue, setPredictionQueue] = useState([]);
	const [isPredictingAll, setIsPredictingAll] = useState(false);

	const handleClearFiles = useCallback(() => {
		setFiles([]);
		setFileOverLimit(false);
		setDuplicateFile(false);
		clearPredictResult();
		setSelectedFileKey(null);
		setPredictingFileKey(null);
		setPredictionQueue([]);
		setIsPredictingAll(false);
	}, [clearPredictResult]);

	const resetState = useCallback(() => {
		handleClearFiles();
		clearPredictConfig();
	}, [handleClearFiles, clearPredictConfig]);

	useEffect(() => {
		if (!visible) {
			resetState();
		}
	}, [visible, resetState]);

	useEffect(() => {
		if (selectedFileKey) {
			const selectedFile = files.find((f) => f.key === selectedFileKey);
			if (!selectedFile?.predicted) {
				clearPredictResult();
			}
		}
	}, [selectedFileKey, files, clearPredictResult]);

	useEffect(() => {
		if (visible && item) {
			previewModel(item.modelid);
		}
	}, [visible, item, previewModel]);

	useEffect(() => {
		const handleResult = async () => {
			if (!result || !predictingFileKey) return;

			const currentFile = files.find((f) => f.key === predictingFileKey);
			const isCSVFile = currentFile?.type?.includes('csv');

			if (isCSVFile) {
				// Handle CSV prediction results
				let chartData = [];
				if (result?.timeseries_result) {
					try {
						const response = await fetch(result?.timeseries_result);
						const csvText = await response.text();
						const parsedData = Papa.parse(csvText, { header: true, skipEmptyLines: true }).data;
						chartData = parsedData;
					} catch (error) {
						console.error('Error fetching or parsing CSV:', error);
					}
				}

				setFiles((currentFiles) =>
					currentFiles.map((f) => {
						if (f.key === predictingFileKey) {
							return {
								...f,
								class: 'Predicted',
								predict: 'Completed',
								accuracy: 'CSV',
								predicted: true,
								predictionResult: result?.timeseries_result,
								chartData: chartData,
								mae: result?.mae,
								rmse: result?.rmse,
							};
						}
						return f;
					})
				);
			} else {
				// Handle non-CSV prediction results
				const maxPredict = Math.max(...result?.value);
				const maxIndex = result?.value.indexOf(maxPredict);
				const predictedClass = config?.modelclass[maxIndex];

				setFiles((currentFiles) =>
					currentFiles.map((f) => {
						if (f.key === predictingFileKey) {
							return {
								...f,
								class: predictedClass,
								classIndex: maxIndex,
								predict: maxPredict.toFixed(4),
								f1_score: result.f1_score,
								precision: result.precision,
								recall: result.recall,
								accuracy: result.accuracy,
								confusionMatrix: result.confusion_matrix,
								predicted: true,
								predictionResult: result?.value,
							};
						}
						return f;
					})
				);
			}
			setPredictingFileKey(null);
			clearPredictResult();
		};

		handleResult();
	}, [result, predictingFileKey, config, clearPredictResult, files]);

	useEffect(() => {
		if (isPredictingAll && !predicting && predictionQueue.length > 0) {
			const nextFileKey = predictionQueue[0];
			const fileToPredict = files.find((f) => f.key === nextFileKey)?.file;

			if (fileToPredict) {
				setPredictingFileKey(nextFileKey);
				predictValue(fileToPredict, config?.modelid);
			}
			setPredictionQueue((prev) => prev.slice(1));
		} else if (isPredictingAll && predictionQueue.length === 0 && !predicting) {
			setIsPredictingAll(false);
		}
	}, [predictionQueue, isPredictingAll, predicting, files, config, predictValue]);

	const getAcceptedType = useMemo(() => {
		if (!config) return { image: null, type: '', accepted_type: '' };

		const splitType = config?.fileType.split(', ');
		const imageTypes = IMAGE_TYPES;
		const audioTypes = AUDIO_TYPES;
		const acceptedType = splitType.map((type) => `.${type.toLowerCase()}`).join(',');

		let typeDetails;
		if (imageTypes.some((type) => splitType.includes(type))) {
			typeDetails = { image: ImageUpload, type: 'image' };
		} else if (audioTypes.some((type) => splitType.includes(type))) {
			typeDetails = { image: AudioUpload, type: 'audio' };
		} else {
			typeDetails = { image: CSVUpload, type: 'timeseries' };
		}

		return { ...typeDetails, accepted_type: acceptedType };
	}, [config]);

	const beforeUpload = useCallback(
		(file) => {
			setFileOverLimit(false);
			setDuplicateFile(false);

			if (files.some((f) => f.name === file.name)) {
				setDuplicateFile(true);
				return Upload.LIST_IGNORE;
			}

			const isFileOver = file.size / 1024 / 1024 < parseFloat(config?.maxSize);
			if (!isFileOver) {
				setFileOverLimit(true);
				return Upload.LIST_IGNORE;
			}

			getBase64(file, (imageUrl, data) => {
				const newFile = {
					key: file.uid,
					src: imageUrl,
					type: file.type,
					name: file.name,
					file,
					data,
				};
				setFiles((prev) => [...prev, newFile]);
				if (files.length === 0) {
					setSelectedFileKey(newFile.key);
				}
			});

			return false;
		},
		[files, config?.maxSize]
	);

	const uploadProps = useMemo(
		() => ({
			name: 'file',
			multiple: true,
			showUploadList: false,
			beforeUpload,
			style: { maxHeight: `${CHART_HEIGHT}px` },
			accept: getAcceptedType.accepted_type,
			directory: false,
		}),
		[beforeUpload, getAcceptedType.accepted_type]
	);

	const menu = useMemo(
		() => (
			<Menu>
				<Menu.Item key="upload-files">
					<Upload {...uploadProps}>Upload File(s)</Upload>
				</Menu.Item>
				{(getAcceptedType.type === 'image' || getAcceptedType.type === 'audio') && (
					<Menu.Item key="upload-folder">
						<Upload {...{ ...uploadProps, directory: true }}>Upload Folder</Upload>
					</Menu.Item>
				)}
				{files.length > 0 && (
					<Menu.Item key="delete-all" danger onClick={handleClearFiles}>
						Delete All
					</Menu.Item>
				)}
			</Menu>
		),
		[uploadProps, getAcceptedType.type, files, handleClearFiles]
	);

	const handlePredictAll = () => {
		const unpredictedFileKeys = files.filter((f) => !f.predicted).map((f) => f.key);
		if (unpredictedFileKeys.length > 0) {
			setPredictionQueue(unpredictedFileKeys);
			setIsPredictingAll(true);
		}
	};

	const handleDownload = async (path) => {
		if (!path) return;

		try {
			const response = await fetch(path);
			const blob = await response.blob();
			const url = createBlobUrl(blob);
			downloadFile(url, 'confusion-matrix.png');
			revokeBlobUrl(url);
		} catch (error) {
			console.error('Error downloading file:', error);
		}
	};

	const handleDownloadCSV = async () => {
		const selectedFile = files.find((f) => f.key === selectedFileKey);
		if (!selectedFile?.predictionResult) return;

		try {
			const response = await fetch(selectedFile.predictionResult);
			const csvText = await response.text();
			const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
			const url = createBlobUrl(blob);
			const filename = `${selectedFile.name.replace('.csv', '')}_prediction_results.csv`;
			downloadFile(url, filename);
		} catch (error) {
			console.error('Error downloading CSV:', error);
		}
	};

	const handleDownloadChart = async (chartElement) => {
		if (!chartElement) return;

		try {
			const dataUrl = await toPng(chartElement, {
				backgroundColor: 'white',
				width: CHART_EXPORT_WIDTH,
				height: CHART_EXPORT_HEIGHT,
			});

			const filename = `${selectedFile?.name.replace('.csv', '') || 'chart'}_prediction_chart.png`;
			downloadFile(dataUrl, filename);
		} catch (error) {
			console.error('Error generating chart image:', error);
		}
	};

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				key: 'name',
			},
			{
				title: 'Class',
				dataIndex: 'class',
				key: 'class',
				render: (text) => text || '-',
			},
			{
				title: 'Predict',
				dataIndex: 'predict',
				key: 'predict',
				render: (text, record) => {
					if (record.key === predictingFileKey && predicting) {
						return <Spin size="small" />;
					}
					return text || '-';
				},
			},
			{
				title: 'Accuracy',
				dataIndex: 'accuracy',
				key: 'accuracy',
				render: (text) => text || '-',
			},
		],
		[predictingFileKey, predicting]
	);

	const selectedFile = files.find((f) => f.key === selectedFileKey);
	const resultToShow = selectedFile?.predictionResult || result?.value;
	const allFilesPredicted = files.length > 0 && files.every((f) => f.predicted);

	return (
		<>
			<StyledModal visible={visible} {...rest} isImage={!!files.length}>
				<Row gutter={[20, 20]}>
					<Col span={12}>
						<AttachFileHeader>
							<Info style={{ marginTop: 0 }}>Attach file</Info>
							<Dropdown overlay={menu} trigger={['click']}>
								<Button type="text" icon={<MoreOutlined />} />
							</Dropdown>
						</AttachFileHeader>
						<Space direction="vertical" style={{ width: '100%' }}>
							{fileOver && (
								<Alert
									message={`File must be smaller than ${config?.maxSize.toUpperCase()}`}
									showIcon
									closable
									type="error"
								/>
							)}
							{duplicateFile && (
								<Alert
									message="File with the same name already uploaded."
									type="warning"
									showIcon
									closable
									onClose={() => setDuplicateFile(false)}
								/>
							)}
							{files.length > 1 ? (
								<FileTable
									files={files}
									columns={columns}
									setSelectedFileKey={setSelectedFileKey}
									selectedFileKey={selectedFileKey}
								/>
							) : (
								<UploadArea
									files={files}
									uploadProps={uploadProps}
									getAcceptedType={getAcceptedType}
									config={config}
									onClearFiles={handleClearFiles}
								/>
							)}
						</Space>
						<Info>
							Accepted file type: <span>{config?.fileType}</span>
						</Info>
						<PredictBtnContainer>
							<Space>
								<Button
									type="primary"
									onClick={handlePredictAll}
									loading={isPredictingAll}
									disabled={allFilesPredicted || predicting || isPredictingAll || files.length === 0}
								>
									Predict
								</Button>
							</Space>
						</PredictBtnContainer>
					</Col>
					<Col span={12}>
						<PredictionPanel
							config={config}
							resultToShow={resultToShow}
							onShowConfusionMatrix={() => setConfusionMatrixVisible(true)}
							selectedFile={selectedFile}
							onDownloadCSV={handleDownloadCSV}
							onDownloadChart={handleDownloadChart}
						/>
					</Col>
				</Row>
			</StyledModal>
			<ConfusionMatrixModal
				confusionMatrix={selectedFile?.confusionMatrix}
				visible={confusionMatrixVisible}
				onCancel={() => setConfusionMatrixVisible(false)}
				onDownload={() => handleDownload(selectedFile?.confusionMatrix)}
			/>
		</>
	);
};

const mapStateToProps = ({ modelReducer }) => ({
	config: modelReducer.preview,
	predicting: modelReducer.predicting,
	result: modelReducer.result,
});

const mapDispatchToProps = {
	previewModel: previewModel.request,
	predictValue: predictValue.request,
	clearPredictResult,
	clearPredictConfig,
};

export default connect(mapStateToProps, mapDispatchToProps)(PreviewModal);

const StyledModal = styled(BaseModal).attrs({
	title: 'Test ML Model',
	titlePosition: 'left',
	footer: null,
})`
	min-width: 964px;
	${({ isImage }) =>
		!isImage &&
		`
        .ant-modal-content {
		    padding-bottom: 60px;
	    }
    `}
`;

const AttachFileHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
`;

const StyledTable = styled(Table)`
	.ant-table-tbody > tr > td {
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.ant-table-tbody > tr.not-predicted > td {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr.predicted-class-0 > td {
		background-color: #ffcccb;
	}

	.ant-table-tbody > tr.predicted-class-1 > td {
		background-color: #d9f7be;
	}

	.ant-table-tbody > tr.not-predicted:hover > td,
	.ant-table-tbody > tr.not-predicted.selected-row > td {
		background-color: #e8e8e8;
	}

	.ant-table-tbody > tr.predicted-class-0:hover > td,
	.ant-table-tbody > tr.predicted-class-0.selected-row > td {
		background-color: #ffbaba;
	}

	.ant-table-tbody > tr.predicted-class-1:hover > td,
	.ant-table-tbody > tr.predicted-class-1.selected-row > td {
		background-color: #cbf0ac;
	}
`;

const ResultTag = styled(Tag)`
	font-size: 10px;
	border-radius: 20px;
	color: ${TEXT_COLOR_PRIMARY};
	background-color: #fff;
	border: 1px solid ${PREDICTED_COLOR};
	padding: 0px 6px;
`;
const CMButton = styled(Button).attrs({})`
	border: none !important;
	box-shadow: none !important;
	font-size: 10px;
	font-weight: normal;
	border-bottom: 1px solid ${PREDICTED_COLOR} !important;
	border-radius: 0 !important;
	padding: 0px 6px;

	:hover {
		border: none !important;
		box-shadow: none !important;
		background-color: #fff !important;
		border-bottom: 2px solid ${PREDICTED_COLOR} !important;
	}
	:focus {
		border: none !important;
		box-shadow: none !important;
		background-color: #fff !important;
	}
`;

const Title = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: ${TEXT_COLOR_PRIMARY};
`;

const Info = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${TEXT_COLOR_SECONDARY};
	margin-top: 10px;

	span {
		color: ${TEXT_COLOR_PRIMARY};
	}
`;

const PredictBtnContainer = styled.div`
	text-align: center;
	margin: 20px 0;
`;

const BaseContainer = styled.div`
	border: 1px solid ${BORDER_COLOR};
	border-radius: 6px;
	position: relative;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const ImageContainer = styled(BaseContainer)``;

const PreviewContainer = styled(BaseContainer)`
	width: 100%;
	height: ${CHART_HEIGHT}px;
	flex-direction: column;
`;

const Dragger = styled(Upload.Dragger).attrs({
	openFileDialogOnClick: false,
	style: {
		border: '1px solid #fff',
		background: '#fff',
	},
})`
	.ant-upload-btn {
		padding: 0px !important;
	}
`;
